import { useState } from 'react';
import FieldEditor from './FieldEditor';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FieldConfig } from '../types/template';

interface DraggableFieldListProps {
  fields: FieldConfig[];
  onChange: (fields: FieldConfig[]) => void;
  demoMode?: boolean;
}

function SortableField({ field, onToggle, onRemove, onEdit }: { field: FieldConfig; onToggle: (id: string) => void; onRemove: (id: string) => void; onEdit: (id: string) => void }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '12px 15px',
          marginBottom: '8px',
          backgroundColor: field.enabled ? 'white' : '#f5f5f5',
          border: '1px solid #ddd',
          borderRadius: '6px',
          touchAction: 'none',
          transition: 'box-shadow 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        {/* Draggable left side: icon + label + variable */}
        <div
          {...listeners}
          style={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            cursor: field.enabled ? 'grab' : 'not-allowed',
            marginRight: '12px',
          }}
        >
          {/* Drag indicator */}
          <div
            style={{
              padding: '0 12px 0 0',
              fontSize: '20px',
              color: field.enabled ? '#999' : '#ddd',
              lineHeight: '1',
            }}
          >
            ⋮⋮
          </div>

          {/* Label */}
          <span
            style={{
              flex: 1,
              fontSize: '14px',
              fontWeight: field.enabled ? '500' : '400',
              color: field.enabled ? '#1f2937' : '#9ca3af',
              marginRight: '8px',
            }}
          >
            {field.label}
          </span>

          {/* Variable name */}
          <code
            style={{
              fontSize: '11px',
              color: field.enabled ? '#6b7280' : '#d1d5db',
              backgroundColor: field.enabled ? '#f3f4f6' : '#f9fafb',
              padding: '3px 8px',
              borderRadius: '4px',
            }}
          >
            {`{{${field.variable}}}`}
          </code>
        </div>

        {field.enabled ? (
          <>
            {/* Edit button */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(field.id);
              }}
              style={{
                marginRight: '6px',
                padding: '6px 12px',
                fontSize: '12px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              }}
            >
              Edit
            </button>
            {/* Remove button */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(field.id);
              }}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              }}
            >
              Remove
            </button>
          </>
        ) : (
          /* Add button */
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle(field.id);
            }}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            Add
          </button>
        )}
      </div>
    </div>
  );
}

export default function DraggableFieldList({ fields, onChange, demoMode }: DraggableFieldListProps) {
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);
  const [fieldSearch, setFieldSearch] = useState('');
  
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = fields.findIndex((field) => field.id === active.id);
      const newIndex = fields.findIndex((field) => field.id === over.id);

      const reordered = arrayMove(fields, oldIndex, newIndex);
      
      // Reassign row numbers based on new order, grouped by column
      // This ensures the grid reflects the visual order in the field list
      const rowCountByCol: Map<number, number> = new Map();
      
      const withUpdatedPositions = reordered.map((field) => {
        if (!field.enabled) {
          return field;
        }
        const col = field.col ?? 0;
        const row = rowCountByCol.get(col) ?? 0;
        rowCountByCol.set(col, row + 1);
        return { ...field, row, col };
      });

      onChange(withUpdatedPositions);
    }
  };

  const handleToggle = (id: string) => {
    onChange(
      fields.map((field) =>
        field.id === id ? { ...field, enabled: !field.enabled } : field
      )
    );
  };

  const handleRemove = (id: string) => {
    onChange(
      fields.map((field) =>
        field.id === id ? { ...field, enabled: false } : field
      )
    );
  };

  const handleEdit = (id: string) => {
    setEditingFieldId(id);
  };

  const handleFieldChange = (updatedField: FieldConfig) => {
    onChange(
      fields.map((field) =>
        field.id === updatedField.id ? updatedField : field
      )
    );
  };

  const enabledFields = fields.filter(f => f.enabled);
  const availableFields = fields.filter(f => !f.enabled);
  
  // Categorize available fields
  const categorizedFields = {
    'Personal': availableFields.filter(f => ['profilePicture', 'name', 'firstName', 'lastName'].includes(f.id)),
    'Job Info': availableFields.filter(f => ['jobTitle', 'department', 'employeeId', 'employeeType', 'employeeHireDate'].includes(f.id)),
    'Contact': availableFields.filter(f => ['email', 'phone', 'mobile', 'faxNumber'].includes(f.id)),
    'Location': availableFields.filter(f => ['officeLocation', 'streetAddress', 'city', 'state', 'postalCode', 'country', 'usageLocation'].includes(f.id)),
    'Company': availableFields.filter(f => ['logo', 'secondaryLogo', 'companyName', 'company', 'website', 'vat', 'address'].includes(f.id)),
    'Custom Attributes': availableFields.filter(f => f.id.startsWith('extensionAttribute')),
    'Shared Mailbox': availableFields.filter(f => f.id.startsWith('shared')),
    'Other': availableFields.filter(f => 
      !['logo', 'secondaryLogo', 'profilePicture', 'name', 'firstName', 'lastName', 'jobTitle', 'department', 'employeeId', 'employeeType', 'employeeHireDate',
        'email', 'phone', 'mobile', 'faxNumber', 'officeLocation', 'streetAddress', 'city', 'state', 'postalCode', 'country', 'usageLocation',
        'companyName', 'company', 'website', 'vat', 'address'].includes(f.id) && !f.id.startsWith('extensionAttribute') && !f.id.startsWith('shared')
    ),
  };

  const filteredCategories = fieldSearch
    ? Object.entries(categorizedFields).reduce((acc, [category, fields]) => {
        const filtered = fields.filter(f => 
          f.label.toLowerCase().includes(fieldSearch.toLowerCase()) ||
          f.variable.toLowerCase().includes(fieldSearch.toLowerCase())
        );
        if (filtered.length > 0) acc[category] = filtered;
        return acc;
      }, {} as Record<string, FieldConfig[]>)
    : categorizedFields;

  return (
    <div>
      <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <label style={{ fontWeight: '600', fontSize: '15px', color: '#1f2937' }}>Signature Fields</label>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
            {enabledFields.length} active • Drag to reorder
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            onClick={() => setShowAddFieldModal(true)}
            style={{
              padding: '8px 16px',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            Add Field
          </button>
          <button
            type="button"
            onClick={() => {
              const newId = `spacer_${Date.now()}`;
              onChange([...fields, { id: newId, label: 'Spacer', variable: 'spacer', enabled: true }]);
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            Add Spacer
          </button>
          <button
            type="button"
            onClick={() => {
              const newId = `text_${Date.now()}`;
              const textFieldCount = fields.filter(f => f.id.startsWith('text_')).length;
              onChange([...fields, { id: newId, label: `Custom Text ${textFieldCount + 1}`, variable: 'customText', enabled: true }]);
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            Add Text Field
          </button>
          <button
            type="button"
            onClick={() => {
              const columnBreak: FieldConfig = {
                id: `columnBreak_${Date.now()}`,
                label: '─── Column Break ───',
                variable: 'columnBreak',
                enabled: true,
                columnSpacing: 250, // Default left position
              };
              onChange([...fields, columnBreak]);
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: '#8b5cf6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
            title="Insert a column break to split signature into multiple columns"
          >
            Column Break
          </button>
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={enabledFields.map((f) => f.id)}
          strategy={verticalListSortingStrategy}
        >
          {enabledFields.map((field) => (
            <SortableField
              key={field.id}
              field={field}
              onToggle={handleToggle}
              onRemove={handleRemove}
              onEdit={handleEdit}
            />
          ))}
        </SortableContext>
      </DndContext>

      {/* Add Field Modal */}
      {showAddFieldModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={() => setShowAddFieldModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '600px',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ padding: '20px', borderBottom: '1px solid #e5e7eb' }}>
              <h3 style={{ margin: '0 0 12px', fontSize: '18px', fontWeight: '600' }}>Add Fields</h3>
              <input
                type="text"
                placeholder="Search fields..."
                value={fieldSearch}
                onChange={(e) => setFieldSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                }}
              />
            </div>

            {/* Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
              {Object.entries(filteredCategories).map(([category, categoryFields]) => 
                categoryFields.length > 0 && (
                  <div key={category} style={{ marginBottom: '24px' }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: '13px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {category}
                    </h4>
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {categoryFields.map((field) => (
                        <div
                          key={field.id}
                          style={{
                            padding: '12px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: 'white',
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: '500', fontSize: '14px', color: '#1f2937' }}>
                              {field.label}
                            </div>
                            <code style={{ fontSize: '11px', color: '#6b7280' }}>
                              {`{{${field.variable}}}`}
                            </code>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              handleToggle(field.id);
                              // Don't close modal, keep it open for adding more fields
                            }}
                            style={{
                              padding: '6px 12px',
                              fontSize: '12px',
                              backgroundColor: '#10b981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '600',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            }}
                          >
                            Add
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              )}
              {Object.values(filteredCategories).every(arr => arr.length === 0) && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                  {fieldSearch ? 'No fields found' : 'All fields are already added'}
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: '20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => {
                  setShowAddFieldModal(false);
                  setFieldSearch('');
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Field Editor Modal */}
      {editingFieldId && (() => {
        const field = fields.find((f) => f.id === editingFieldId)!;
        const fieldIndex = enabledFields.findIndex((f) => f.id === editingFieldId);
        // If field not found in enabledFields (shouldn't happen), default to 1
        const rowNumber = fieldIndex >= 0 ? fieldIndex + 1 : 1;
        return (
          <FieldEditor
            field={field}
            fieldIndex={rowNumber}
            onChange={handleFieldChange}
            onClose={() => setEditingFieldId(null)}
            demoMode={demoMode}
          />
        );
      })()}
    </div>
  );
}
